import { OktaAuth, AccessToken } from "@okta/okta-auth-js";
import { redirect } from "@tanstack/react-router";
import { Feature } from "../hooks/useFeatureFlag";

/**
 * Centralized authorization service for handling authentication and authorization checks.
 * Provides a single source of truth for all auth-related operations.
 */
export class AuthorizationService {
  constructor(
    private oktaAuth: OktaAuth,
    private requiredScopes: string[]
  ) {}

  /**
   * Checks if the user is currently authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    return this.oktaAuth.isAuthenticated();
  }

  /**
   * Checks if the access token contains the required scope
   */
  async hasRequiredScope(): Promise<boolean> {
    const { accessToken } = await this.oktaAuth.tokenManager.getTokens();
    if (!accessToken) {
      return false;
    }

    const requiredScope = this.requiredScopes[this.requiredScopes.length - 1];
    return accessToken.scopes.includes(requiredScope);
  }

  /**
   * Synchronously checks if a token contains the required scope.
   * Use this when you already have the token (e.g., in React hooks/components).
   */
  hasRequiredScopeSync(token: AccessToken): boolean {
    const requiredScope = this.requiredScopes[this.requiredScopes.length - 1];
    return token.scopes.includes(requiredScope);
  }

  /**
   * Checks if the user has access to a specific feature
   */
  hasFeatureAccess(enabledFeatures: Feature[] | undefined, feature: Feature): boolean {
    if (!enabledFeatures) {
      return false;
    }
    return enabledFeatures.includes(feature);
  }

  /**
   * Enforces authentication - redirects to login if not authenticated
   * @param location - Current location for redirect after login
   */
  async enforceAuthentication(location: string): Promise<void> {
    const isAuth = await this.isAuthenticated();
    
    if (!isAuth) {
      console.log('[AuthorizationService] User not authenticated, redirecting to login');
      throw redirect({
        to: "/login",
        search: {
          redirect: location,
        },
      });
    }
  }

  /**
   * Enforces authorization - checks authentication and required scope
   * @param location - Current location for redirect
   */
  async enforceAuthorization(location: string): Promise<void> {
    await this.enforceAuthentication(location);

    const hasScope = await this.hasRequiredScope();
    if (!hasScope) {
      const { accessToken } = await this.oktaAuth.tokenManager.getTokens();
      console.warn(
        '[AuthorizationService] Missing required scope. Token scopes:',
        accessToken?.scopes,
        'Required:',
        this.requiredScopes[this.requiredScopes.length - 1]
      );
      throw redirect({
        to: "/no-permission",
      });
    }
  }

  /**
   * Enforces feature access - checks authentication, scope, and feature flag
   * @param enabledFeatures - List of enabled features for the user
   * @param feature - Feature to check access for
   * @param location - Current location for redirect
   */
  async enforceFeatureAccess(
    enabledFeatures: Feature[] | undefined,
    feature: Feature,
    location: string
  ): Promise<void> {
    await this.enforceAuthorization(location);

    if (!this.hasFeatureAccess(enabledFeatures, feature)) {
      console.warn('[AuthorizationService] Feature access denied:', feature);
      throw redirect({ to: "/" });
    }
  }

  /**
   * Gets the current access token
   */
  async getAccessToken(): Promise<AccessToken | undefined> {
    const tokens = await this.oktaAuth.tokenManager.getTokens();
    return tokens.accessToken;
  }
}

/**
 * Creates an authorization service instance
 * @param oktaAuth - Initialized OktaAuth instance
 * @param requiredScopes - Array of required OAuth scopes
 */
export function createAuthorizationService(
  oktaAuth: OktaAuth,
  requiredScopes: string[]
): AuthorizationService {
  return new AuthorizationService(oktaAuth, requiredScopes);
}
